/**
 * @file useGoldRecharge.js
 * @description 全局橘子币充值：购卷入口走 recharge/preview，用户中心走 package/list
 * @module features/user/composables/useGoldRecharge
 */

import { computed, ref } from 'vue'
import { ElMessage } from 'element-plus'
import { getAuthToken } from '@/shared/utils/auth'
import {
  getApiErrorMessageByCode,
  ERR_GOLD_PACKAGE_DISABLED,
  ERR_NOT_LOGIN,
  WECHAT_PAY_DISABLED_HINT,
} from '@/shared/utils/apiErrorCodes'
import {
  registerGoldRechargeOpener,
  requestRetryVolPayAfterRecharge,
  requestVolPayRechargePreview,
} from '@/shared/utils/payDialogBridge'
import { createPayIdempotencyKey } from '@/shared/utils/payIdempotency'
import { createWechatOrderPay } from '@/shared/composables/useWechatOrderPay'
import { getStoredUser, setStoredUser } from '@/shared/utils/storage'
import { emitGoldRechargePaid } from '@/shared/utils/authEvents'
import {
  getStoredUserId,
  getStoredUserPhone,
  hasPendingTipOAuthAutoRetried,
  markPendingTipOAuthAutoRetried,
  clearPendingTipOAuthAutoRetried,
} from '@/shared/utils/wxMpOAuth'
import { useUserStore } from '@/stores/user'
import {
  fetchGoldPackageListApi,
  GoldWxMpOAuthRequiredError,
  prepareGoldWechatApi,
  queryGoldWechatOrderStatusApi,
} from '../api/goldRechargeApi'
import {
  clearPendingGoldPayResume,
  consumePendingGoldPayResume,
  isGoldPayResumeSessionDone,
  markGoldPayResumeSessionDone,
  peekPendingGoldPayResume,
  savePendingGoldPayResume,
} from '../utils/goldPayPendingOAuth'

const visible = ref(false)
const listLoading = ref(false)
const errorMessage = ref('')
const packages = ref(/** @type {import('../api/goldRechargeTypes').GoldPackageVo[]} */ ([]))
const selectedPackageCode = ref('')
const needRechargeGold = ref(0)
const balanceGold = ref(0)
const agreementTitle = ref('橘子币充值协议')
const agreementCode = ref('GOLD_RECHARGE')
const source = ref(/** @type {'vol-pay'|'user-center'} */ ('user-center'))

const EMPTY_PACKAGE_HINT = '暂无可用充值档位'

/** @type {{ source: string, comicId?: string, volId?: string, bulkType?: string, fetchPreview?: Function, onPaid?: Function }|null} */
let openContext = null
let idempotencyKey = ''
let resumeInFlight = false

/**
 * @param {unknown} err
 * @returns {string}
 */
function resolveGoldError(err) {
  const raw = err instanceof Error && err.message ? err.message : ''
  if (raw.includes('微信支付未开')) return WECHAT_PAY_DISABLED_HINT
  const code = err && typeof err === 'object' && 'code' in err ? err.code : null
  const byCode = getApiErrorMessageByCode(code)
  if (byCode) return byCode
  if (raw) return raw
  return '操作失败，请稍后再试'
}

function resetIdempotencyKey() {
  idempotencyKey = ''
}

function ensureIdempotencyKey() {
  if (!idempotencyKey) idempotencyKey = createPayIdempotencyKey()
  return idempotencyKey
}

/**
 * 用户中心预选 defaultFlag；购卷窗优先 recommended，否则同样按 defaultFlag / sort。
 * @param {import('../api/goldRechargeTypes').GoldPackageVo[]} list
 * @param {'vol-pay'|'user-center'} [from]
 * @returns {string}
 */
function pickDefaultPackage(list, from = source.value) {
  if (!list.length) return ''
  if (from === 'vol-pay') {
    const recommended = list.find((item) => item.recommended)
    if (recommended) return recommended.code
  }
  const flagged = list.find((item) => item.defaultFlag)
  if (flagged) return flagged.code
  return [...list].sort((a, b) => (b.sort || 0) - (a.sort || 0))[0]?.code || list[0].code
}

const wechatPay = createWechatOrderPay({
  prepareFn: async (payload) => {
    try {
      return await prepareGoldWechatApi(payload)
    } catch (err) {
      const code = err && typeof err === 'object' && 'code' in err ? Number(err.code) : null
      if (code === ERR_GOLD_PACKAGE_DISABLED) {
        void loadPackages(selectedPackageCode.value)
      }
      throw err
    }
  },
  queryFn: queryGoldWechatOrderStatusApi,
  buildPreparePayload(scene, key) {
    return {
      packageCode: selectedPackageCode.value,
      scene,
      idempotencyKey: key,
    }
  },
  onBeforeJsapiPrepare() {
    if (!openContext) return
    savePendingGoldPayResume({
      source: openContext.source,
      packageCode: selectedPackageCode.value,
      comicId: openContext.comicId,
      volId: openContext.volId,
      bulkType: openContext.bulkType,
      idempotencyKey: ensureIdempotencyKey(),
      expectedUserId: getStoredUserId(),
      expectedUserPhone: getStoredUserPhone(),
      preOAuthSession: getStoredUser(),
    })
  },
  onPrepareSuccess() {
    clearPendingGoldPayResume()
    clearPendingTipOAuthAutoRetried()
  },
  onPaid(statusVo) {
    const userStore = useUserStore()
    if (statusVo?.balanceGold != null) {
      const current = userStore.user || {}
      userStore.setUser({
        ...current,
        userGoldInfo: {
          ...(current.userGoldInfo || {}),
          goldAmount: statusVo.balanceGold,
        },
      })
    }
    void userStore.fetchUserInfo()
    emitGoldRechargePaid()
    const onPaid = openContext?.onPaid
    if (typeof onPaid === 'function') {
      onPaid(statusVo)
    } else if (openContext?.source === 'vol-pay') {
      void requestRetryVolPayAfterRecharge()
    }
  },
  resolveError: resolveGoldError,
  isOAuthError: (err) => err instanceof GoldWxMpOAuthRequiredError,
  getOAuthUrl: (err) => (err instanceof GoldWxMpOAuthRequiredError ? err.oauthAuthorizeUrl : ''),
})

function resetDialog() {
  wechatPay.resetPayState()
  listLoading.value = false
  errorMessage.value = ''
  packages.value = []
  selectedPackageCode.value = ''
  needRechargeGold.value = 0
  balanceGold.value = 0
  agreementTitle.value = '橘子币充值协议'
  agreementCode.value = 'GOLD_RECHARGE'
  source.value = 'user-center'
  openContext = null
  resetIdempotencyKey()
}

/**
 * @param {string} [presetCode]
 */
async function loadPackages(presetCode = '') {
  if (!openContext) return
  listLoading.value = true
  errorMessage.value = ''
  try {
    if (openContext.source === 'vol-pay') {
      const data = typeof openContext.fetchPreview === 'function'
        ? await openContext.fetchPreview()
        : await requestVolPayRechargePreview({
            comicId: openContext.comicId,
            volId: openContext.volId,
            bulkType: openContext.bulkType || 'NONE',
          })
      if (!data) throw new Error('获取充值档位失败')
      const list = Array.isArray(data?.packages) ? data.packages : []
      packages.value = [...list].sort((a, b) => (b.sort || 0) - (a.sort || 0))
      needRechargeGold.value = Number(data?.needRechargeGold) || 0
      balanceGold.value = Number(data?.balanceGold) || 0
      agreementTitle.value = data?.agreementTitle || '橘子币充值协议'
      agreementCode.value = data?.agreementCode || 'GOLD_RECHARGE'
    } else {
      const data = await fetchGoldPackageListApi()
      const list = Array.isArray(data?.list) ? data.list : []
      packages.value = [...list].sort((a, b) => (b.sort || 0) - (a.sort || 0))
      needRechargeGold.value = 0
      balanceGold.value = Number(data?.balanceGold) || 0
      agreementTitle.value = data?.agreementTitle || '橘子币充值协议'
      agreementCode.value = data?.agreementCode || 'GOLD_RECHARGE'
    }
    const preset = typeof presetCode === 'string' ? presetCode.trim() : ''
    if (preset && packages.value.some((item) => item.code === preset)) {
      selectedPackageCode.value = preset
    } else {
      selectedPackageCode.value = pickDefaultPackage(packages.value)
    }
    if (!packages.value.length) {
      errorMessage.value = EMPTY_PACKAGE_HINT
      ElMessage.warning(EMPTY_PACKAGE_HINT)
    }
  } catch (err) {
    const code = err && typeof err === 'object' && 'code' in err ? Number(err.code) : null
    if (code === ERR_GOLD_PACKAGE_DISABLED) {
      errorMessage.value = resolveGoldError(err)
      packages.value = []
      return
    }
    if (code === ERR_NOT_LOGIN) {
      errorMessage.value = resolveGoldError(err)
      return
    }
    errorMessage.value = resolveGoldError(err)
  } finally {
    listLoading.value = false
  }
}

/**
 * @param {{
 *   source: 'vol-pay'|'user-center',
 *   comicId?: string,
 *   volId?: string,
 *   bulkType?: string,
 *   fetchPreview?: Function,
 *   packageCode?: string,
 *   onPaid?: Function,
 * }} context
 * @returns {boolean}
 */
export function openGoldRecharge(context) {
  if (!getAuthToken()) {
    ElMessage.warning('请先登录后再操作')
    return false
  }
  openContext = {
    source: context.source === 'vol-pay' ? 'vol-pay' : 'user-center',
    comicId: context.comicId ? String(context.comicId) : '',
    volId: context.volId ? String(context.volId) : '',
    bulkType: context.bulkType || 'NONE',
    fetchPreview: context.fetchPreview,
    onPaid: context.onPaid,
  }
  source.value = openContext.source
  wechatPay.resetPayState()
  resetIdempotencyKey()
  visible.value = true
  void loadPackages(context.packageCode || '')
  return true
}

export function closeGoldRecharge() {
  visible.value = false
  resetDialog()
}

/**
 * @param {string} code
 */
function selectPackage(code) {
  if (selectedPackageCode.value !== code) {
    selectedPackageCode.value = code
    resetIdempotencyKey()
  }
  wechatPay.errorMessage.value = ''
  errorMessage.value = ''
}

async function submitGoldRecharge() {
  if (!packages.value.length) {
    errorMessage.value = EMPTY_PACKAGE_HINT
    ElMessage.warning(EMPTY_PACKAGE_HINT)
    return
  }
  if (!selectedPackageCode.value) {
    errorMessage.value = '请选择充值档位'
    return
  }
  await wechatPay.startPay(ensureIdempotencyKey())
}

function backToSelect() {
  wechatPay.stopPolling()
  wechatPay.payStep.value = 'select'
  wechatPay.qrDataUrl.value = ''
  wechatPay.errorMessage.value = ''
  resetIdempotencyKey()
}

/**
 * OAuth 回站后续办充值
 * @param {{ fromOAuthError?: string|null, keepPending?: boolean }} [options]
 * @returns {Promise<boolean>}
 */
export async function resumePendingGoldPayAfterOAuth(options = {}) {
  const { fromOAuthError = null, keepPending = false } = options
  if (resumeInFlight) return false
  const pending = keepPending ? peekPendingGoldPayResume() : consumePendingGoldPayResume()
  if (!pending || !getAuthToken()) return false
  const resumeSessionId =
    typeof pending.resumeSessionId === 'string' ? pending.resumeSessionId.trim() : ''
  if (resumeSessionId && isGoldPayResumeSessionDone(resumeSessionId)) return false

  resumeInFlight = true
  const currentPhone = getStoredUserPhone()
  const currentUserId = getStoredUserId()
  const phoneMismatch =
    Boolean(pending.expectedUserPhone) && pending.expectedUserPhone !== currentPhone
  const userIdMismatch =
    Boolean(pending.expectedUserId) && currentUserId && pending.expectedUserId !== currentUserId
  if (phoneMismatch || userIdMismatch) {
    if (pending.preOAuthSession && typeof pending.preOAuthSession === 'object') {
      setStoredUser(pending.preOAuthSession)
    }
    openGoldRecharge({
      source: pending.source === 'vol-pay' ? 'vol-pay' : 'user-center',
      comicId: pending.comicId,
      volId: pending.volId,
      bulkType: pending.bulkType,
      packageCode: pending.packageCode,
    })
    errorMessage.value = '微信授权未绑定到当前账号，请关闭弹窗后重新充值'
    resumeInFlight = false
    return false
  }

  if (fromOAuthError) {
    const retriable = fromOAuthError === 'missing_code' || fromOAuthError === 'invalid_callback'
    if (!retriable || hasPendingTipOAuthAutoRetried()) {
      openGoldRecharge({
        source: pending.source === 'vol-pay' ? 'vol-pay' : 'user-center',
        comicId: pending.comicId,
        volId: pending.volId,
        bulkType: pending.bulkType,
        packageCode: pending.packageCode,
      })
      errorMessage.value = '微信授权未完成，请重试充值'
      resumeInFlight = false
      return false
    }
    markPendingTipOAuthAutoRetried()
  } else {
    clearPendingTipOAuthAutoRetried()
  }

  if (pending.idempotencyKey) idempotencyKey = pending.idempotencyKey

  try {
    const opened = openGoldRecharge({
      source: pending.source === 'vol-pay' ? 'vol-pay' : 'user-center',
      comicId: pending.comicId,
      volId: pending.volId,
      bulkType: pending.bulkType,
      packageCode: pending.packageCode,
    })
    if (!opened) return false
    await loadPackages(pending.packageCode)
    if (packages.value.some((item) => item.code === pending.packageCode)) {
      selectedPackageCode.value = pending.packageCode
      if (resumeSessionId) markGoldPayResumeSessionDone(resumeSessionId)
      await wechatPay.startPay(ensureIdempotencyKey())
    }
    return true
  } finally {
    resumeInFlight = false
  }
}

registerGoldRechargeOpener(openGoldRecharge)

export function useGoldRecharge() {
  const selectedPackage = computed(
    () => packages.value.find((item) => item.code === selectedPackageCode.value) || null
  )
  const showNeedRecharge = computed(
    () => source.value === 'vol-pay' && needRechargeGold.value > 0
  )
  const showRecommended = computed(() => source.value === 'vol-pay')
  const combinedError = computed(() => errorMessage.value || wechatPay.errorMessage.value)
  const canSubmitPay = computed(
    () =>
      !wechatPay.loading.value &&
      !listLoading.value &&
      packages.value.length > 0 &&
      Boolean(selectedPackageCode.value)
  )

  return {
    visible,
    listLoading,
    errorMessage: combinedError,
    packages,
    selectedPackageCode,
    selectedPackage,
    needRechargeGold,
    balanceGold,
    agreementTitle,
    agreementCode,
    source,
    showNeedRecharge,
    showRecommended,
    qrDataUrl: wechatPay.qrDataUrl,
    payLoading: wechatPay.loading,
    payStep: wechatPay.payStep,
    isConfirmingPayment: wechatPay.isConfirmingPayment,
    canSubmitPay,
    openGoldRecharge,
    closeGoldRecharge,
    selectPackage,
    submitGoldRecharge,
    backToSelect,
    confirmPaymentManually: wechatPay.confirmPaymentManually,
  }
}
